import { AsyncPipe, I18nPluralPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { I18NEXT_SERVICE, I18NextModule, ITranslationService } from 'angular-i18next';
import { BehaviorSubject, from, map, mergeMap, Observable } from 'rxjs';
import { BaseComponent } from 'src/app/_shared/components/base/base.component';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { OpenDialogService } from 'src/app/common/open-dialog/open-dialog.service';
import { PeopleAvatarListComponent } from 'src/app/_shared/components/people-avatar-list/people-avatar-list.component';
import { RouteTranslateService } from 'src/app/common/route-translate/route-translate.service';
import { UserService } from 'src/app/common/user/user.service';
import { getDefaultModalSettings } from 'src/app/common/utils/helpers';
import { User } from 'src/types/graphql';
import { LandFollowersGQL } from './followers.query.generated';

@Component({
  selector: 'sharry-land-followers',
  standalone: true,
  imports: [I18nPluralPipe, AsyncPipe, IconComponent, I18NextModule, PeopleAvatarListComponent],
  templateUrl: './followers.component.html',
  styleUrls: ['./followers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FollowersComponent extends BaseComponent implements OnChanges, OnDestroy {
  @Input() slug: string;
  @Input() myFavourite: Partial<User>;
  @Input() divider = false;
  followers$: Observable<Partial<User>[]>;
  followersSubj: BehaviorSubject<Partial<User>[]>;
  typeToken: {
    followers: Partial<Partial<User>[]>;
  };
  itemPluralMapping = {
    followers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      '=1': 'followers.follow-this-page',
      other: 'followers.follow-this-page.plural',
    },
  };
  loading = false;

  constructor(
    private openDialogService: OpenDialogService,
    private landFollowersGQL: LandFollowersGQL,
    @Inject(I18NEXT_SERVICE) private translateService: ITranslationService,
    private userService: UserService,
    private router: Router,
    private routeTranslateService: RouteTranslateService,
  ) {
    super();
    this.followersSubj = new BehaviorSubject([]);
    this.followers$ = this.followersSubj.asObservable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.slug?.currentValue) {
      this.subscriptions.push(
        this.landFollowersGQL
          .fetch({
            slug: this.slug,
          })
          .pipe(map(r => r?.data?.landBySlug?.followers as Partial<User>[]))
          .subscribe({
            next: followers => {
              this.followersSubj.next([
                ...followers.filter(f => f.id === this.userService.user?.id),
                ...followers.filter(f => f.id !== this.userService.user?.id),
              ]);
            },
          }),
      );
    }
    if (changes?.myFavourite) {
      if (!changes?.myFavourite?.currentValue) {
        this.followersSubj.next(this.followersSubj.value?.filter(f => f.id !== this.userService.user?.id));
      } else {
        const isPresent = this.followersSubj.value.find(f => f.id === changes?.myFavourite?.currentValue?.id);
        if (isPresent) {
          this.followersSubj.next([
            ...this.followersSubj.value.filter(f => f.id === this.userService.user?.id),
            ...this.followersSubj.value.filter(f => f.id !== this.userService.user?.id),
          ]);
        } else {
          this.followersSubj.next([
            changes?.myFavourite?.currentValue,
            ...this.followersSubj.value.filter(f => f.id !== this.userService.user?.id),
          ]);
        }
      }
    }
  }

  onPeopleClick() {
    const people = this.followersSubj.value.map(f => ({ user: f }));
    const data = {
      data: {
        people,
        headerText:
          people?.length > 1
            ? this.translateService.t('followers.follow-this-page.plural', { people: people?.length })
            : this.translateService.t('followers.follow-this-page', { people: people?.length }),
      },
    };
    from(import('src/app/_shared/dialogs/people-dialog/people-dialog.component'))
      .pipe(
        mergeMap(({ PeopleDialogComponent }) =>
          this.openDialogService
            .openDialog(PeopleDialogComponent, {
              ...getDefaultModalSettings(),
              ...data,
            })
            .afterClosed(),
        ),
      )
      .subscribe({
        next: user => {
          if (user) {
            this.router.navigate([this.routeTranslateService.translateRoute('profile'), user.slug]);
          }
        },
      });
  }

  ngOnDestroy(): void {
    this.followersSubj.complete();
  }
}
